"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useActivityLogFilters } from "@/hooks/use-activity-log-filters";
import { ACTIVITY_PATH_LABELS } from "@/lib/activity-log-labels";
import { ChevronDownIcon, FilterIcon, XIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { format } from "date-fns";
import type {
  ActivityActionTypeValue,
  ActivityPathValue,
  ActivityResultValue,
} from "@/types/activity-log";

function parseDateFromFilter(
  value: string | null | undefined,
): Date | undefined {
  if (!value || value.length < 10) return undefined;
  const s = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined;
  return new Date(s + "T12:00:00");
}

/** Extract time "HH:mm:ss" from ISO string (e.g. 2025-03-10T14:30:00), or return default. */
function parseTimeFromFilter(
  value: string | null | undefined,
  defaultTime: string,
): string {
  if (!value || !value.includes("T")) return defaultTime;
  const timePart = value.split("T")[1];
  if (!timePart) return defaultTime;
  const match = timePart.match(/^(\d{2}:\d{2})(?::(\d{2}))?/);
  if (!match) return defaultTime;
  return match[2] != null ? `${match[1]}:${match[2]}` : `${match[1]}:00`;
}

const PATH_OPTIONS: { value: "" | ActivityPathValue; label: string }[] = [
  { value: "", label: "Tous" },
  { value: "RETAIL", label: ACTIVITY_PATH_LABELS.RETAIL },
  { value: "AVS", label: ACTIVITY_PATH_LABELS.AVS },
  { value: "SYSTEM", label: ACTIVITY_PATH_LABELS.SYSTEM },
];

const ACTION_TYPE_OPTIONS: {
  value: "" | ActivityActionTypeValue;
  label: string;
}[] = [
  { value: "", label: "Tous" },
  { value: "CONNECTION", label: "Connexion" },
  { value: "DISCONNECTION", label: "Déconnexion" },
  { value: "KVPS_CHANGE", label: "Changement KVPS" },
  { value: "VEHICLE_SEARCH_INIT", label: "Init recherche véhicule" },
  { value: "DRAFT_CREATE", label: "Création brouillon" },
  { value: "DRAFT_UPDATE", label: "Modification brouillon" },
  { value: "DRAFT_DELETE", label: "Suppression brouillon" },
  { value: "CTM_VALIDATION", label: "Validation CTM" },
  { value: "IMPORT_FILE_RCE_CNIT", label: "Import fichiers RCE/CNIT" },
  { value: "RCE_CNIT_UPDATE", label: "Modification RCE/CNIT" },
  { value: "RCE_CNIT_DELETE", label: "Suppression RCE/CNIT" },
  { value: "RAPPROCHEMENT_MANUAL", label: "Rapprochement manuel d'un VIN" },
  { value: "ECOC_IMPORT_RELANCE", label: "Relance en masse ECOC / COC" },
  { value: "ECOC_BATCH_RELANCE", label: "Batch relance ECOC / COC" },
  {
    value: "CAR_VIEW_STATUS_CHECK",
    label: "Vérification du statut du véhicule",
  },
  { value: "FDM_REQUEST_CREATE", label: "Création d'une demande FDM" },
  { value: "FDM_REQUESTS_IMPORT", label: "Import des demandes FDM (batch)" },
  { value: "FUD_CREATE", label: "Création FUD" },
];

const RESULT_OPTIONS: { value: "" | ActivityResultValue; label: string }[] = [
  { value: "", label: "Tous" },
  { value: "OK", label: "OK" },
  { value: "KO_METIER", label: "KO métier" },
  { value: "KO_TECH", label: "KO technique" },
];

function ActivityLogFilters({ distinctKvps }: { distinctKvps: string[] }) {
  const { filters, setFilters } = useActivityLogFilters();
  const [openFrom, setOpenFrom] = useState(false);
  const [openTo, setOpenTo] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(() =>
    parseDateFromFilter(filters.dateFrom),
  );
  const [dateTo, setDateTo] = useState<Date | undefined>(() =>
    parseDateFromFilter(filters.dateTo),
  );
  const [timeFrom, setTimeFrom] = useState(() =>
    parseTimeFromFilter(filters.dateFrom, "00:00:00"),
  );
  const [timeTo, setTimeTo] = useState(() =>
    parseTimeFromFilter(filters.dateTo, "00:00:00"),
  );
  const [path, setPath] = useState<"" | ActivityPathValue>(filters.path ?? "");
  const [kvps, setKvps] = useState(filters.kvps ?? "");
  const [actionType, setActionType] = useState<"" | ActivityActionTypeValue>(
    filters.actionType ?? "",
  );
  const [result, setResult] = useState<"" | ActivityResultValue>(
    filters.result ?? "",
  );
  const [q, setQ] = useState(filters.q ?? "");

  const handleApply = useCallback(() => {
    const tFrom = timeFrom.trim() || "00:00:00";
    const tTo = timeTo.trim() || "00:00:00";
    const padTime = (t: string) => (t.length <= 5 ? `${t}:00` : t);
    setFilters({
      dateFrom: dateFrom
        ? `${format(dateFrom, "yyyy-MM-dd")}T${padTime(tFrom)}`
        : null,
      dateTo: dateTo ? `${format(dateTo, "yyyy-MM-dd")}T${padTime(tTo)}` : null,
      path: path || null,
      kvps: kvps || null,
      actionType: actionType || null,
      result: result || null,
      q: q.trim() || null,
    });
  }, [
    dateFrom,
    dateTo,
    timeFrom,
    timeTo,
    path,
    kvps,
    actionType,
    result,
    q,
    setFilters,
  ]);

  const handleResetGlobal = useCallback(() => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setTimeFrom("00:00:00");
    setTimeTo("00:00:00");
    setPath("");
    setKvps("");
    setActionType("");
    setResult("");
    setQ("");
    setFilters({
      dateFrom: null,
      dateTo: null,
      path: null,
      kvps: null,
      actionType: null,
      result: null,
      q: null,
      p: 1,
    });
  }, [setFilters]);

  const clearDu = useCallback(() => {
    setDateFrom(undefined);
    setTimeFrom("00:00:00");
    setFilters({ dateFrom: null });
  }, [setFilters]);

  const clearAu = useCallback(() => {
    setDateTo(undefined);
    setTimeTo("23:59:59");
    setFilters({ dateTo: null });
  }, [setFilters]);

  const clearPath = useCallback(() => {
    setPath("");
    setFilters({ path: null });
  }, [setFilters]);

  const clearKvps = useCallback(() => {
    setKvps("");
    setFilters({ kvps: null });
  }, [setFilters]);

  const clearActionType = useCallback(() => {
    setActionType("");
    setFilters({ actionType: null });
  }, [setFilters]);

  const clearResult = useCallback(() => {
    setResult("");
    setFilters({ result: null });
  }, [setFilters]);

  const hasAnyFilter =
    dateFrom != null ||
    dateTo != null ||
    path !== "" ||
    kvps !== "" ||
    actionType !== "" ||
    result !== "" ||
    q.trim() !== "";

  const kvpsComboboxItems = useMemo(() => [...distinctKvps], [distinctKvps]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FilterIcon className="text-primary size-5" />
          Filtres
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 *:min-w-0 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          <Field className="max-w-66 min-w-52">
            <div className="flex w-full items-center justify-between gap-2">
              <FieldLabel htmlFor="al-dateFrom">Du</FieldLabel>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-auto gap-1 px-1.5 disabled:opacity-50"
                aria-label="Réinitialiser Du"
                onClick={clearDu}
                disabled={dateFrom == null}
              >
                <XIcon className="size-3.5" />
                Réinitialiser
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Popover open={openFrom} onOpenChange={setOpenFrom}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    id="al-dateFrom"
                    className="border-input hover:bg-accent hover:text-accent-foreground min-w-0 shrink-0 justify-between border bg-transparent font-normal shadow-xs sm:w-32"
                  >
                    <span className="truncate">
                      {dateFrom ? format(dateFrom, "yyyy-MM-dd") : "Date"}
                    </span>
                    <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto overflow-hidden p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    captionLayout="dropdown"
                    defaultMonth={dateFrom}
                    onSelect={(date) => {
                      setDateFrom(date);
                      setOpenFrom(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
              <Input
                type="time"
                id="al-timeFrom"
                step="1"
                value={timeFrom}
                onChange={(e) => setTimeFrom(e.target.value)}
                className="border-input h-9 w-24 shrink-0 appearance-none border bg-transparent text-sm shadow-xs [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              />
            </div>
          </Field>
          <Field className="max-w-66 min-w-56">
            <div className="flex w-full items-center justify-between gap-2">
              <FieldLabel htmlFor="al-dateTo">Au</FieldLabel>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-auto gap-1 px-1.5 disabled:opacity-50"
                aria-label="Réinitialiser Au"
                onClick={clearAu}
                disabled={dateTo == null}
              >
                <XIcon className="size-3.5" />
                Réinitialiser
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Popover open={openTo} onOpenChange={setOpenTo}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    id="al-dateTo"
                    className="border-input hover:bg-accent hover:text-accent-foreground min-w-0 shrink-0 justify-between border bg-transparent font-normal shadow-xs sm:w-34"
                  >
                    <span className="truncate">
                      {dateTo ? format(dateTo, "yyyy-MM-dd") : "Date"}
                    </span>
                    <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto overflow-hidden p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    captionLayout="dropdown"
                    defaultMonth={dateTo}
                    onSelect={(date) => {
                      setDateTo(date);
                      setOpenTo(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
              <Input
                type="time"
                id="al-timeTo"
                step="1"
                value={timeTo}
                onChange={(e) => setTimeTo(e.target.value)}
                className="border-input h-9 w-24 shrink-0 appearance-none border bg-transparent text-sm shadow-xs [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              />
            </div>
          </Field>
          <Field className="max-w-52 min-w-44">
            <div className="flex w-full items-center justify-between gap-2">
              <FieldLabel htmlFor="al-path">Parcours</FieldLabel>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-auto gap-1 px-1.5 disabled:opacity-50"
                aria-label="Réinitialiser Parcours"
                onClick={clearPath}
                disabled={path === ""}
              >
                <XIcon className="size-3.5" />
                Réinitialiser
              </Button>
            </div>
            <Select
              value={path || "__all__"}
              onValueChange={(v) =>
                setPath((v === "__all__" ? "" : v) as "" | ActivityPathValue)
              }
            >
              <SelectTrigger id="al-path" className="h-9 w-full min-w-44">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                {PATH_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value || "all"}
                    value={opt.value || "__all__"}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field className="max-w-52 min-w-44">
            <div className="flex w-full items-center justify-between gap-2">
              <FieldLabel htmlFor="al-kvps">KVPS</FieldLabel>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-auto gap-1 px-1.5 disabled:opacity-50"
                aria-label="Réinitialiser KVPS"
                onClick={clearKvps}
                disabled={kvps === ""}
              >
                <XIcon className="size-3.5" />
                Réinitialiser
              </Button>
            </div>
            <Combobox
              items={kvpsComboboxItems}
              value={kvps || null}
              onValueChange={(v) => setKvps(v ?? "")}
            >
              <ComboboxInput
                id="al-kvps"
                placeholder="Tous"
                className="h-9 w-full min-w-44"
                showClear={kvps !== ""}
              />
              <ComboboxContent>
                <ComboboxEmpty>Aucun KVPS trouvé</ComboboxEmpty>
                <ComboboxList>
                  {(item) => (
                    <ComboboxItem key={item} value={item}>
                      {item}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </Field>
          <Field className="max-w-52 min-w-44">
            <div className="flex w-full items-center justify-between gap-2">
              <FieldLabel htmlFor="al-actionType">
                Type d&apos;action
              </FieldLabel>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-auto gap-1 px-1.5 disabled:opacity-50"
                aria-label="Réinitialiser Type d'action"
                onClick={clearActionType}
                disabled={actionType === ""}
              >
                <XIcon className="size-3.5" />
                Réinitialiser
              </Button>
            </div>
            <Select
              value={actionType || "__all__"}
              onValueChange={(v) =>
                setActionType(
                  (v === "__all__" ? "" : v) as "" | ActivityActionTypeValue,
                )
              }
            >
              <SelectTrigger id="al-actionType" className="h-9 w-full min-w-44">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                {ACTION_TYPE_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value || "all"}
                    value={opt.value || "__all__"}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field className="max-w-52 min-w-44">
            <div className="flex w-full items-center justify-between gap-2">
              <FieldLabel htmlFor="al-result">Résultat</FieldLabel>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-auto gap-1 px-1.5 disabled:opacity-50"
                aria-label="Réinitialiser Résultat"
                onClick={clearResult}
                disabled={result === ""}
              >
                <XIcon className="size-3.5" />
                Réinitialiser
              </Button>
            </div>
            <Select
              value={result || "__all__"}
              onValueChange={(v) =>
                setResult(
                  (v === "__all__" ? "" : v) as "" | ActivityResultValue,
                )
              }
            >
              <SelectTrigger id="al-result" className="h-9 w-full min-w-44">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                {RESULT_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.value || "all"}
                    value={opt.value || "__all__"}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-md min-w-[200px] space-y-2">
            <Label htmlFor="al-search">
              Recherche (référence, utilisateur, identifiant utilisateur,
              détails)
            </Label>
            <Input
              id="al-search"
              type="search"
              placeholder="Rechercher…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleApply()}
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button onClick={handleApply}>Appliquer</Button>
            <Button
              variant="outline"
              onClick={handleResetGlobal}
              disabled={!hasAnyFilter}
            >
              Réinitialiser
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export { ActivityLogFilters };
